import { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Business from '@mui/icons-material/Business';
import DomainDisabled from '@mui/icons-material/DomainDisabled';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Person from '@mui/icons-material/Person';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import { Control, useWatch } from 'react-hook-form';
import { getDriveLabelPermissionsQuery } from '../../../../api/users-api';
import type { LabelPermissionModel } from '../../../../api/drive-labels-api';
import LabelAppIcon, { LabelApp } from './label-app-icon';
import LabelDetailsPanel from './label-details-panel';
import PreviewField from './preview-field';

/** A disabled option set to "Hide option and disable". */
const isHiddenOption = (option: {
  disabled?: boolean;
  showInApply?: boolean;
}) => !!option.disabled && option.showInApply === false;
import WarningCallout from './warning-callout';
import {
  CreateLabelForm,
  FIELD_TYPE_CONFIG,
  orgPermissionSummary,
  toLabelPermissionRole,
  toPendingDisplayPermissions,
} from './label-schema';

const APP_NAMES: Record<LabelApp, string> = {
  DRIVE: 'Drive and Docs',
  GMAIL: 'Gmail',
};

interface LabelPreviewPanelProps {
  control: Control<CreateLabelForm>;
  labelId: string;
  creator: string;
  lastModified: number;
  /** Shown in the organization permission tooltip, e.g. the primary domain. */
  organizationName: string;
  /** Shown instead of Google's until they're applied; then the label's saved permissions are read. */
  pendingPermissions?: LabelPermissionModel[];
  onEditPermissions: () => void;
  /** The label's status for Label details; Create Label always shows a draft. */
  status?: string;
  /**
   * Edit Label: the published revision's values. When given, Draft and Published tabs let the
   * admin compare the saved changes with what users see now.
   */
  publishedControl?: Control<CreateLabelForm>;
}

/** Field settings that change how the preview control works; a change resets its value. */
const previewKey = (field: CreateLabelForm['fields'][number]) =>
  [
    field.key,
    field.type,
    field.dateFormat,
    field.multipleSelections,
    field.options.map((option) => option.key).join(','),
  ].join('|');

export default function LabelPreviewPanel({
  control,
  labelId,
  creator,
  lastModified,
  organizationName,
  pendingPermissions,
  onEditPermissions,
  status,
  publishedControl,
}: LabelPreviewPanelProps) {
  const [previewApp, setPreviewApp] = useState<LabelApp>('DRIVE');
  const [revision, setRevision] = useState<'draft' | 'published'>('draft');
  const draftValues = useWatch({
    control,
    name: ['title', 'fields', 'driveEnabled', 'gmailEnabled'],
  });
  // Always watched, so hooks run in the same order; without a published revision it's the draft.
  const publishedValues = useWatch({
    control: publishedControl ?? control,
    name: ['title', 'fields', 'driveEnabled', 'gmailEnabled'],
  });
  const showingPublished = !!publishedControl && revision === 'published';
  const [title, fields, driveEnabled, gmailEnabled] = showingPublished
    ? publishedValues
    : draftValues;
  const { data: savedPermissions = [] } = useQuery({
    ...getDriveLabelPermissionsQuery(labelId),
    // Pending permissions are shown instead, so Google's aren't needed until they're applied.
    enabled: !!labelId && !pendingPermissions,
    // Only this page changes the draft's permissions, and each save stores Google's response, so
    // reading them again adds nothing (and right after a write Google can briefly list none).
    staleTime: Infinity,
  });
  const permissions = pendingPermissions
    ? toPendingDisplayPermissions(pendingPermissions)
    : savedPermissions;

  const appEnabled = previewApp === 'DRIVE' ? driveEnabled : gmailEnabled;
  // Edit Label: disabled fields and hidden options can't be applied, so users don't see them.
  const appliableFields = fields
    .filter((field) => !field.disabled)
    .map((field) =>
      field.options.some(isHiddenOption)
        ? {
            ...field,
            options: field.options.filter((option) => !isHiddenOption(option)),
          }
        : field
    );
  const previewFields =
    previewApp === 'GMAIL'
      ? appliableFields.filter(
          (field) => FIELD_TYPE_CONFIG[field.type].gmailSupported
        )
      : appliableFields;
  const hasRequiredFields = previewFields.some((field) => field.required);
  const audience = permissions.find((permission) => permission.audience);
  const principals = permissions.filter((permission) => !permission.audience);

  return (
    <Card sx={{ borderRadius: 2 }}>
      <Stack divider={<Divider />}>
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 500 }}>Preview</Typography>
            <Typography variant="body2" color="text.secondary">
              See how this label will look to users
            </Typography>
          </Box>
          {publishedControl && (
            <Tabs
              value={revision}
              onChange={(_, value) => setRevision(value)}
              aria-label="Preview revision"
            >
              <Tab
                value="draft"
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box component="span">Draft</Box>
                    <Chip label="NEW" size="small" color="primary" />
                  </Stack>
                }
              />
              <Tab value="published" label="Published" />
            </Tabs>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel id="label-preview-app-label">
              Showing preview for
            </InputLabel>
            <Select
              id="label-preview-app"
              labelId="label-preview-app-label"
              label="Showing preview for"
              value={previewApp}
              onChange={(event) =>
                setPreviewApp(event.target.value as LabelApp)
              }
              renderValue={(app) => (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LabelAppIcon app={app} />
                  <Typography variant="body2">{APP_NAMES[app]}</Typography>
                </Stack>
              )}
            >
              {(['DRIVE', 'GMAIL'] as const).map((app) => (
                <MenuItem key={app} value={app}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LabelAppIcon app={app} />
                    <Typography variant="body2">{APP_NAMES[app]}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!appEnabled && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <WarningAmber color="warning" sx={{ fontSize: 18 }} />
              <Typography variant="caption" color="warning.main">
                Label is not enabled in this application
              </Typography>
            </Stack>
          )}
          <Card variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {title.trim() || 'Untitled label'}
              </Typography>
              {appEnabled && hasRequiredFields && (
                <WarningCallout icon={<InfoOutlined sx={{ fontSize: 18 }} />}>
                  <Typography variant="caption">
                    This label contains required fields
                  </Typography>
                </WarningCallout>
              )}
              {!appEnabled || previewFields.length === 0 ? (
                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                  No fields in this label
                </Typography>
              ) : (
                previewFields.map((field) => (
                  <PreviewField key={previewKey(field)} field={field} />
                ))
              )}
            </Stack>
          </Card>
        </Stack>

        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontWeight: 500 }}>Permissions</Typography>
            <Button
              variant="text"
              size="small"
              // "Edit" as written in the design, not MUI's all-caps button text.
              sx={{ textTransform: 'none' }}
              onClick={onEditPermissions}
            >
              Edit
            </Button>
          </Stack>
          <AvatarGroup max={6} sx={{ justifyContent: 'flex-end' }}>
            {/* Like the Google Admin console, the organization is always shown; crossed out when
                  the label is restricted to the people and groups added. */}
            <Tooltip
              title={orgPermissionSummary(
                audience ? toLabelPermissionRole(audience.role) : 'RESTRICTED',
                organizationName
              )}
            >
              <Avatar
                aria-label={
                  audience ? 'Organization access' : 'Organization restricted'
                }
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: audience ? 'primary.light' : 'action.hover',
                }}
              >
                {audience ? (
                  <Business fontSize="small" color="primary" />
                ) : (
                  <DomainDisabled fontSize="small" color="action" />
                )}
              </Avatar>
            </Tooltip>
            {principals.map((principal) => (
              <Tooltip
                key={principal.name ?? principal.email}
                title={principal.email ?? ''}
              >
                <Avatar
                  aria-label={principal.email ?? undefined}
                  sx={{ width: 32, height: 32 }}
                >
                  <Person fontSize="small" />
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Stack>

        <LabelDetailsPanel
          embedded
          labelId={labelId}
          creator={creator}
          lastModified={lastModified}
          status={status}
        />
      </Stack>
    </Card>
  );
}
